import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaAngleLeft,
  FaEllipsisVertical,
  FaTrash,
  FaLock,
  FaPen,
  FaPlus,
} from "react-icons/fa6";
import { useTranslation } from "react-i18next";

const MAX_FIREFLIES = 20;

const PALETTE = {
  amber: {
    hex: "#fbbf24",
    glow: "drop-shadow-[0_0_8px_rgba(251,191,36,0.4)]",
    selectedGlow: "drop-shadow-[0_0_20px_rgba(251,191,36,0.9)]",
    text: "text-amber-100",
  },
  emerald: {
    hex: "#34d399",
    glow: "drop-shadow-[0_0_8px_rgba(52,211,153,0.4)]",
    selectedGlow: "drop-shadow-[0_0_20px_rgba(52,211,153,0.9)]",
    text: "text-emerald-100",
  },
  cyan: {
    hex: "#22d3ee",
    glow: "drop-shadow-[0_0_8px_rgba(34,211,238,0.4)]",
    selectedGlow: "drop-shadow-[0_0_20px_rgba(34,211,238,0.9)]",
    text: "text-cyan-100",
  },
  purple: {
    hex: "#c084fc",
    glow: "drop-shadow-[0_0_8px_rgba(192,132,252,0.4)]",
    selectedGlow: "drop-shadow-[0_0_20px_rgba(192,132,252,0.9)]",
    text: "text-purple-100",
  },
  rose: {
    hex: "#fb7185",
    glow: "drop-shadow-[0_0_8px_rgba(251,113,133,0.4)]",
    selectedGlow: "drop-shadow-[0_0_20px_rgba(251,113,133,0.9)]",
    text: "text-rose-100",
  },
};
const colorKeys = Object.keys(PALETTE);

const DEFAULT_ACTIVITIES = [
  {
    id: "work",
    tKey: "activities.work",
    priority: 1,
    isPrivate: false,
    color: "emerald",
  },
  {
    id: "love",
    tKey: "activities.date",
    priority: 1,
    isPrivate: false,
    color: "rose",
  },
  {
    id: "sport",
    tKey: "activities.sport",
    priority: 2,
    isPrivate: false,
    color: "cyan",
  },
  {
    id: "reading",
    tKey: "activities.reading",
    priority: 2,
    isPrivate: false,
    color: "purple",
  },
  {
    id: "games",
    tKey: "activities.games",
    priority: 3,
    isPrivate: false,
    color: "amber",
  },
  {
    id: "music",
    tKey: "activities.music",
    priority: 3,
    isPrivate: false,
    color: "purple",
  },
  {
    id: "family",
    tKey: "activities.family",
    priority: 1,
    isPrivate: false,
    color: "rose",
  },
  {
    id: "walk",
    tKey: "activities.walk",
    priority: 2,
    isPrivate: false,
    color: "emerald",
  },
  {
    id: "cinema",
    tKey: "activities.cinema",
    priority: 3,
    isPrivate: false,
    color: "cyan",
  },
  {
    id: "cook",
    tKey: "activities.cooking",
    priority: 2,
    isPrivate: false,
    color: "amber",
  },
  {
    id: "rest",
    tKey: "activities.rest",
    priority: 1,
    isPrivate: false,
    color: "cyan",
  },
];

const FireflyBug = ({ colorHex, isSelected }) => {
  const bodyColor = "#4a3629";
  const headColor = "#2d2119";

  return (
    <svg
      viewBox="0 0 24 24"
      className="w-full h-full transition-transform duration-300"
    >
      <circle cx="12" cy="5" r="1.8" fill={headColor} />
      <rect x="10" y="6" width="4" height="6" rx="2" fill={bodyColor} />
      <circle cx="12" cy="16" r="4.5" fill={colorHex} />
      <ellipse
        cx="10"
        cy="11"
        rx="2.5"
        ry="6.5"
        transform="rotate(20 10 11)"
        fill="rgba(255, 255, 255, 0.3)"
        stroke="rgba(255, 255, 255, 0.6)"
        strokeWidth="0.1"
      />
      <ellipse
        cx="14"
        cy="11"
        rx="2.5"
        ry="6.5"
        transform="rotate(-20 14 11)"
        fill="rgba(255, 255, 255, 0.3)"
        stroke="rgba(255, 255, 255, 0.6)"
        strokeWidth="0.1"
      />
      {isSelected && (
        <circle cx="12" cy="16" r="1.8" fill="#ffffff" opacity="0.9" />
      )}
    </svg>
  );
};

function MoodFireflies() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [activities, setActivities] = useState([]);
  const [selectedActivities, setSelectedActivities] = useState([]);

  const [isEditMode, setIsEditMode] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [newText, setNewText] = useState("");
  const [newPriority, setNewPriority] = useState(2);
  const [newColor, setNewColor] = useState("amber");
  const [newIsPrivate, setNewIsPrivate] = useState(false);

  const getFreePosition = (existingItems) => {
    let x, y;
    let attempts = 0;
    let min_dist = 22;
    while (attempts < 200) {
      x = 15 + Math.random() * 70;
      y = 22 + Math.random() * 56;
      let isOverlapping = false;
      for (let item of existingItems) {
        if (!item.left || !item.top) continue;
        const dx = parseFloat(item.left) - x;
        const dy = (parseFloat(item.top) - y) * 1.5;
        if (Math.sqrt(dx * dx + dy * dy) < min_dist) {
          isOverlapping = true;
          break;
        }
      }
      if (!isOverlapping) return { left: `${x}%`, top: `${y}%` };
      attempts++;
      if (attempts % 40 === 0) min_dist -= 2;
    }
    return { left: `${x}%`, top: `${y}%` };
  };

  useEffect(() => {
    const savedActivities = localStorage.getItem("moodActivities");
    let loadedActivities = [];

    if (savedActivities) {
      const parsed = JSON.parse(savedActivities);
      loadedActivities = parsed.map((act) => ({
        ...act,
        rotationDeg:
          act.rotationDeg !== undefined
            ? act.rotationDeg
            : Math.floor(Math.random() * 40 - 20),
      }));
    } else {
      let placed = [];
      loadedActivities = DEFAULT_ACTIVITIES.map((act) => {
        const pos = getFreePosition(placed);
        placed.push(pos);
        return {
          id: act.id,
          tKey: act.tKey,
          priority: act.priority,
          isPrivate: act.isPrivate,
          ...pos,
          color: act.color,
          animDelay: `${Math.random() * -10}s`,
          animDuration: `${8 + Math.random() * 6}s`,
          rotationDeg: Math.floor(Math.random() * 40 - 20),
        };
      });
    }

    setActivities(loadedActivities);
    localStorage.setItem("moodActivities", JSON.stringify(loadedActivities));

    const savedSelected = localStorage.getItem("selectedActivities");
    if (savedSelected) setSelectedActivities(JSON.parse(savedSelected));
  }, []);

  const saveActivitiesToStorage = (newActivities) => {
    setActivities(newActivities);
    localStorage.setItem("moodActivities", JSON.stringify(newActivities));
    localStorage.setItem("moodActivitiesChanged", "true");
  };

  const getActText = (act) => (act.tKey ? t(act.tKey) : act.text);

  const handleOpenAdd = () => {
    if (activities.length >= MAX_FIREFLIES) return;
    setIsMenuOpen(false);
    setEditingId(null);
    setShowDeleteConfirm(false);
    setNewText("");
    setNewPriority(2);
    setNewColor("emerald");
    setNewIsPrivate(false);
    setIsModalOpen(true);
  };

  const handleOpenEditMode = () => {
    setIsMenuOpen(false);
    setIsEditMode(true);
  };

  const handleFireflyClick = (act) => {
    if (isEditMode) {
      setEditingId(act.id);
      setShowDeleteConfirm(false);
      setNewText(getActText(act));
      setNewPriority(act.priority);
      setNewColor(act.color);
      setNewIsPrivate(act.isPrivate || false);
      setIsModalOpen(true);
    } else {
      setSelectedActivities((prev) => {
        const newSelected = prev.includes(act.id)
          ? prev.filter((i) => i !== act.id)
          : [...prev, act.id];
        localStorage.setItem("selectedActivities", JSON.stringify(newSelected));
        return newSelected;
      });
    }
  };

  const handleSaveModal = () => {
    if (!newText.trim()) return;

    if (editingId) {
      const updated = activities.map((act) => {
        if (act.id === editingId) {
          const isUnchangedTranslation =
            act.tKey && newText.trim() === t(act.tKey);
          return {
            ...act,
            text: isUnchangedTranslation ? undefined : newText.trim(),
            tKey: isUnchangedTranslation ? act.tKey : null,
            priority: newPriority,
            color: newColor,
            isPrivate: newIsPrivate,
          };
        }
        return act;
      });
      saveActivitiesToStorage(updated);
    } else {
      const pos = getFreePosition(activities);
      const newFirefly = {
        id: String(Date.now()),
        text: newText.trim(),
        tKey: null,
        priority: Number(newPriority),
        color: newColor,
        isPrivate: newIsPrivate,
        ...pos,
        animDelay: "0s",
        animDuration: "8s",
        rotationDeg: Math.floor(Math.random() * 40 - 20),
      };
      const updated = [...activities, newFirefly];
      saveActivitiesToStorage(updated);

      const newSelected = [...selectedActivities, newFirefly.id];
      setSelectedActivities(newSelected);
      localStorage.setItem("selectedActivities", JSON.stringify(newSelected));
    }
    setIsModalOpen(false);
  };

  const handleDelete = () => {
    if (!editingId) return;
    const updated = activities.filter((act) => act.id !== editingId);
    saveActivitiesToStorage(updated);

    const newSelected = selectedActivities.filter((id) => id !== editingId);
    setSelectedActivities(newSelected);
    localStorage.setItem("selectedActivities", JSON.stringify(newSelected));

    setIsModalOpen(false);
    setShowDeleteConfirm(false);
  };

  const getSizeClass = (priority) => {
    switch (priority) {
      case 1:
        return { svgBox: "w-16 h-16", text: "text-[12px]" };
      case 2:
        return { svgBox: "w-12 h-12", text: "text-[11px]" };
      case 3:
        return { svgBox: "w-9 h-9", text: "text-[10px]" };
      default:
        return { svgBox: "w-12 h-12", text: "text-[11px]" };
    }
  };

  const formatText = (txt) =>
    txt.length > 10 ? txt.substring(0, 10) + "..." : txt;

  return (
    <div className="flex flex-col flex-1 h-full overflow-hidden bg-[#061014] relative select-none font-sans text-white -m-4 p-4">
      {/* CSS-Анімації для світлячків */}
      <style>{`
        @keyframes fireflyFloat {
          0% { transform: translate(0, 0); }
          25% { transform: translate(15px, -15px); }
          50% { transform: translate(0, -25px); }
          75% { transform: translate(-15px, -15px); }
          100% { transform: translate(0, 0); }
        }
        .firefly-anim {
          animation-name: fireflyFloat;
          animation-timing-function: ease-in-out;
          animation-iteration-count: infinite;
        }
        .edit-wiggle {
          animation: editWiggle 0.3s ease-in-out infinite;
        }
        @keyframes editWiggle {
          0%, 100% { transform: translate(-50%, -50%) rotate(-5deg); }
          50% { transform: translate(-50%, -50%) rotate(5deg); }
        }
      `}</style>

      <div
        className="absolute inset-0 pointer-events-none z-0"
        style={{
          background:
            "radial-gradient(circle at 50% 100%, oklch(30.2% 0.056 229.695) 0%, #061014 60%)",
        }}
      />
      <div className="absolute inset-0 opacity-20 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] pointer-events-none z-0"></div>

      {/* HEADER */}
      <div className="relative w-full pt-6 z-50 flex flex-col gap-2">
        {/* Текст по центру зверху */}
        <p className="text-2xl font-semibold text-center truncate pointer-events-none px-4">
          {isEditMode ? t("fireflies.titleEdit") : t("fireflies.title")}
        </p>

        {/* Кнопка справа під текстом */}
        <div className="w-full flex justify-end px-2 relative z-50">
          {isEditMode ? (
            <button
              onClick={() => setIsEditMode(false)}
              className="text-emerald-400 font-bold px-4 py-2"
            >
              {t("fireflies.done")}
            </button>
          ) : (
            <div className="relative">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="p-2"
              >
                <FaEllipsisVertical className="w-7 h-7 text-white" />
              </button>

              {/* Випадаюче меню (з z-50 для перекриття підкладки) */}
              {isMenuOpen && (
                <div className="absolute right-2 top-full mt-1 w-52 bg-gray-800 rounded-2xl shadow-2xl border border-emerald-900/50 overflow-hidden animate-fade-in z-50">
                  <button
                    onClick={handleOpenAdd}
                    disabled={activities.length >= MAX_FIREFLIES}
                    className={`w-full text-left px-5 py-4 text-sm font-semibold border-b border-gray-700 flex items-center gap-3 transition-colors ${
                      activities.length >= MAX_FIREFLIES
                        ? "text-red-400 bg-red-900/10 opacity-70 cursor-not-allowed"
                        : "hover:bg-gray-700 text-white"
                    }`}
                  >
                    <FaPlus
                      className={
                        activities.length >= MAX_FIREFLIES
                          ? "text-red-400"
                          : "text-emerald-400"
                      }
                    />
                    {activities.length >= MAX_FIREFLIES
                      ? t("fireflies.limitReached")
                      : t("fireflies.addEvent")}
                  </button>
                  <button
                    onClick={handleOpenEditMode}
                    className="w-full text-left px-5 py-4 text-sm font-semibold hover:bg-gray-700 flex items-center gap-3 transition-colors text-white"
                  >
                    <FaPen className="text-emerald-400" /> {t("fireflies.edit")}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Підкладка для закриття меню (перехоплює кліки повз меню) */}
      {isMenuOpen && (
        <div
          className="absolute inset-0 z-40"
          onClick={() => setIsMenuOpen(false)}
        />
      )}

      {/* Галявина */}
      <div className="flex-1 relative w-full h-full z-10 mt-2">
        {activities.map((act) => {
          const isSelected = selectedActivities.includes(act.id);
          const size = getSizeClass(act.priority);
          const paletteConfig = PALETTE[act.color] || PALETTE.emerald;
          const displayText = getActText(act);

          return (
            <button
              key={act.id}
              onClick={() => handleFireflyClick(act)}
              className={`absolute flex flex-col items-center justify-center transform -translate-x-1/2 -translate-y-1/2 ${isEditMode ? "edit-wiggle" : ""}`}
              style={{
                top: act.top,
                left: act.left,
                zIndex: isSelected || isEditMode ? 30 : 10,
              }}
            >
              <div
                className="relative flex flex-col items-center justify-center firefly-anim"
                style={{
                  animationDelay: act.animDelay || "0s",
                  animationDuration: act.animDuration || "12s",
                  animationPlayState:
                    isSelected || isEditMode ? "paused" : "running",
                }}
              >
                <div
                  className={`transition-all duration-500 ease-out flex items-center justify-center ${size.svgBox} ${isSelected ? `scale-125 ${paletteConfig.selectedGlow}` : paletteConfig.glow}`}
                >
                  <div
                    className="w-full h-full transition-transform duration-300"
                    style={{ transform: `rotate(${act.rotationDeg}deg)` }}
                  >
                    <FireflyBug
                      colorHex={paletteConfig.hex}
                      isSelected={isSelected}
                    />
                  </div>
                </div>
                <span
                  className={`absolute top-[90%] whitespace-nowrap font-bold drop-shadow-[0_2px_4px_rgba(0,0,0,1)] transition-colors duration-300 ${size.text} ${isSelected ? "text-white" : paletteConfig.text}`}
                >
                  {formatText(displayText)}{" "}
                  {act.isPrivate && (
                    <FaLock className="inline w-2.5 h-2.5 mb-1 text-gray-400 opacity-80" />
                  )}
                </span>
              </div>
            </button>
          );
        })}
      </div>

      {/* НИЖНІЙ БЛОК */}
      <div className="relative z-20 mt-auto pt-4 pb-2">
        {isEditMode ? (
          <div className="w-full h-[60px] bg-white/5 border border-emerald-900/50 rounded-lg flex items-center justify-center font-bold text-emerald-400 uppercase tracking-widest shadow-inner">
            {activities.length} / {MAX_FIREFLIES} {t("fireflies.counter")}
          </div>
        ) : (
          <Link
            to="/mood-result"
            className="block text-center text-3xl font-bold rounded-lg py-3 w-full shadow-[0_0_20px_rgba(16,185,129,0.3)] transition-all duration-300 bg-gradient-to-tr from-emerald-500 via-cyan-500 to-emerald-500 bg-gradient-size animate-gradient-move text-white"
          >
            {selectedActivities.length > 0
              ? t("continue") || "Далі"
              : t("skip") || "Пропустити"}
          </Link>
        )}
      </div>

      {/* Модалка CRUD */}
      {isModalOpen && (
        <div
          className="absolute inset-0 bg-black/70 backdrop-blur-md z-[100] flex flex-col justify-end"
          onClick={(e) => e.target === e.currentTarget && setIsModalOpen(false)}
        >
          <div className="bg-[#0f172a] rounded-t-[32px] p-6 pb-12 flex flex-col gap-5 border-t border-emerald-900 shadow-[0_-10px_40px_rgba(0,0,0,0.8)] animate-fade-in">
            <div className="flex justify-between items-center">
              <h3 className="text-xl font-bold text-white">
                {editingId ? t("fireflies.modalEdit") : t("fireflies.modalNew")}
              </h3>
            </div>

            <input
              type="text"
              maxLength={24}
              placeholder={t("fireflies.placeholder")}
              value={newText}
              onChange={(e) => setNewText(e.target.value)}
              className="bg-black/40 border border-emerald-900/50 p-4 rounded-xl outline-none text-emerald-50 focus:border-emerald-500 transition-colors w-full shadow-inner"
            />

            <div>
              <p className="mb-2 text-sm text-emerald-200/60">
                {t("fireflies.colorLabel")}
              </p>
              <div className="flex justify-between w-full px-2">
                {colorKeys.map((cKey) => (
                  <button
                    key={cKey}
                    onClick={() => setNewColor(cKey)}
                    className={`w-10 h-10 rounded-full transition-transform ${newColor === cKey ? "scale-125 border-2 border-white" : "opacity-60 hover:opacity-100"}`}
                    style={{
                      backgroundColor: PALETTE[cKey].hex,
                      boxShadow:
                        newColor === cKey
                          ? `0 0 15px ${PALETTE[cKey].hex}80`
                          : `0 0 5px ${PALETTE[cKey].hex}40`,
                    }}
                  />
                ))}
              </div>
            </div>

            <div>
              <p className="mb-2 text-sm text-emerald-200/60">
                {t("fireflies.priorityLabel")}
              </p>
              <div className="flex gap-2">
                {[
                  { val: 1, label: t("fireflies.priorityHigh") },
                  { val: 2, label: t("fireflies.priorityMedium") },
                  { val: 3, label: t("fireflies.priorityLow") },
                ].map((lvl) => (
                  <button
                    key={lvl.val}
                    onClick={() => setNewPriority(lvl.val)}
                    className={`flex-1 py-3 rounded-xl text-sm font-medium border transition-all ${
                      newPriority === lvl.val
                        ? "bg-emerald-500/20 border-emerald-400 text-emerald-100 shadow-[inset_0_0_10px_rgba(16,185,129,0.3)]"
                        : "bg-white/5 border-white/10 text-gray-400"
                    }`}
                  >
                    {lvl.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between bg-white/5 p-4 rounded-xl border border-white/5">
              <div>
                <p className="font-medium text-white flex items-center gap-2">
                  <FaLock className="text-gray-400" />{" "}
                  {t("fireflies.privateEvent")}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  {t("fireflies.privateDesc")}
                </p>
              </div>
              <button
                onClick={() => setNewIsPrivate(!newIsPrivate)}
                className={`w-12 h-6 rounded-full transition-colors relative shadow-inner ${newIsPrivate ? "bg-emerald-500" : "bg-gray-600"}`}
              >
                <div
                  className={`w-5 h-5 bg-white rounded-full absolute top-[2px] transition-transform ${newIsPrivate ? "translate-x-6" : "translate-x-1"} shadow-md`}
                />
              </button>
            </div>

            <div className="flex flex-col gap-3 mt-2">
              {showDeleteConfirm ? (
                <div className="flex flex-col gap-3">
                  <p className="text-center text-red-400 font-medium mb-1">
                    {t("fireflies.confirmDeleteDesc")}
                  </p>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowDeleteConfirm(false)}
                      className="flex-1 py-4 rounded-xl bg-gray-700 font-bold text-white transition-colors"
                    >
                      {t("fireflies.cancel")}
                    </button>
                    <button
                      onClick={handleDelete}
                      className="flex-1 py-4 rounded-xl bg-red-500 font-bold text-white shadow-[0_0_15px_rgba(239,68,68,0.4)] transition-all"
                    >
                      {t("fireflies.confirmDelete")}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {editingId && (
                    <button
                      onClick={() => setShowDeleteConfirm(true)}
                      className="w-full py-3 rounded-xl bg-red-500/10 text-red-400 font-medium hover:bg-red-500/20 transition-colors border border-red-500/20 flex items-center justify-center gap-2"
                    >
                      <FaTrash /> {t("fireflies.delete")}
                    </button>
                  )}
                  <div className="flex gap-3">
                    <button
                      onClick={() => setIsModalOpen(false)}
                      className="flex-1 py-4 rounded-xl bg-white/5 font-bold text-white border border-white/5 hover:bg-white/10 transition-colors"
                    >
                      {t("fireflies.cancel")}
                    </button>
                    <button
                      onClick={handleSaveModal}
                      disabled={!newText.trim()}
                      className="flex-1 py-4 rounded-xl bg-emerald-500 font-bold text-white shadow-[0_0_15px_rgba(16,185,129,0.4)] disabled:opacity-50 transition-all"
                    >
                      {t("fireflies.save")}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MoodFireflies;
